/**
 * Copy text to the clipboard.
 *
 * Prefers the async Clipboard API when available, falls back to
 * a textarea + execCommand("copy") for non-secure contexts or
 * older browsers. Returns true on success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
	if (navigator.clipboard) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// Permission denied or non-secure context — fall through to legacy path.
		}
	}

	// Legacy fallback. Append inside an open MUI Dialog (if any) so focus traps
	// don't rip the selection away before execCommand runs.
	const host = document.querySelector<HTMLElement>('[role="dialog"]') ?? document.body;
	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.top = "0";
	textarea.style.left = "0";
	textarea.style.width = "1px";
	textarea.style.height = "1px";
	textarea.style.padding = "0";
	textarea.style.border = "none";
	textarea.style.outline = "none";
	textarea.style.boxShadow = "none";
	textarea.style.background = "transparent";
	textarea.style.opacity = "0";
	host.appendChild(textarea);
	textarea.focus();
	textarea.select();
	textarea.setSelectionRange(0, text.length);
	try {
		return document.execCommand("copy");
	} catch {
		return false;
	} finally {
		host.removeChild(textarea);
	}
}
